/**
 * Centralized CKEditor 5 setup using the new installation methods (v45+).
 *
 * Replaces the old custom UMD build at `ckeditor5-v3/`. All editor consumers
 * (Collaboration.vue, PreviewWrapper.vue, newSummary.vue) import the editor
 * class and base config from here and merge their own toolbar/extraPlugins on
 * top.
 *
 * Imports use the `/dist/index.js` files of each `@ckeditor/*` package — those
 * have CSS and theme imports pre-bundled at publish time. Importing from the
 * package root resolves to `src/index.js` which expects CKEditor's custom
 * postcss theme importer (only present in the legacy custom-build pipeline).
 */

// Webpack 4 (vue-cli 4.5) doesn't follow the `exports` field in package.json,
// so import the CSS via explicit /dist/ paths instead of the documented
// `ckeditor5/ckeditor5.css` short form.
import 'ckeditor5/dist/ckeditor5.css';
import 'ckeditor5-premium-features/dist/ckeditor5-premium-features.css';

import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic/dist/index.js';
import { Alignment } from '@ckeditor/ckeditor5-alignment/dist/index.js';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat/dist/index.js';
import { Autosave } from '@ckeditor/ckeditor5-autosave/dist/index.js';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote/dist/index.js';
import {
	Bold,
	Italic,
	Strikethrough,
	Subscript,
	Superscript,
	Underline,
} from '@ckeditor/ckeditor5-basic-styles/dist/index.js';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services/dist/index.js';
import { Essentials } from '@ckeditor/ckeditor5-essentials/dist/index.js';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace/dist/index.js';
import {
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
} from '@ckeditor/ckeditor5-font/dist/index.js';
import { Highlight } from '@ckeditor/ckeditor5-highlight/dist/index.js';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line/dist/index.js';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed/dist/index.js';
import {
	DataFilter,
	DataSchema,
	GeneralHtmlSupport,
} from '@ckeditor/ckeditor5-html-support/dist/index.js';
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent/dist/index.js';
import { TextPartLanguage } from '@ckeditor/ckeditor5-language/dist/index.js';
import { Link } from '@ckeditor/ckeditor5-link/dist/index.js';
import { List, ListProperties } from '@ckeditor/ckeditor5-list/dist/index.js';
import { PageBreak } from '@ckeditor/ckeditor5-page-break/dist/index.js';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph/dist/index.js';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office/dist/index.js';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format/dist/index.js';
import { StandardEditingMode } from '@ckeditor/ckeditor5-restricted-editing/dist/index.js';
import {
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersMathematical,
	SpecialCharactersText,
} from '@ckeditor/ckeditor5-special-characters/dist/index.js';
import { Style } from '@ckeditor/ckeditor5-style/dist/index.js';
import { WordCount } from '@ckeditor/ckeditor5-word-count/dist/index.js';

// Premium plugins — imported individually to avoid pulling AI/markdown deps.
import { Comments } from '@ckeditor/ckeditor5-comments/dist/index.js';
import {
	RevisionHistory,
	RevisionsRepository,
} from '@ckeditor/ckeditor5-revision-history/dist/index.js';
import {
	TrackChanges,
	TrackChangesData,
} from '@ckeditor/ckeditor5-track-changes/dist/index.js';

import { WProofreader } from '@webspellchecker/wproofreader-ckeditor5';

const builtinPlugins = [
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	CloudServices,
	Comments,
	DataFilter,
	DataSchema,
	Essentials,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	GeneralHtmlSupport,
	Highlight,
	HorizontalLine,
	HtmlEmbed,
	Indent,
	IndentBlock,
	Italic,
	Link,
	List,
	ListProperties,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	RemoveFormat,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersMathematical,
	SpecialCharactersText,
	StandardEditingMode,
	Strikethrough,
	Style,
	Subscript,
	Superscript,
	TextPartLanguage,
	Underline,
	WordCount,
	TrackChanges,
	TrackChangesData,
	WProofreader,
	Autosave,
];

// Subclass ClassicEditor so we can attach `defaultConfig` and `builtinPlugins`.
// CKEditor merges both into the runtime config passed to `editor.create(...)`,
// so consumers don't need to repeat plugins / licenseKey / table / wproofreader
// in every component.
class Editor extends ClassicEditor {}

Editor.builtinPlugins = builtinPlugins;

export const defaultEditorConfig = {
	language: 'en',
	licenseKey: process.env.VUE_APP_CKEDITOR_LICENSE_KEY,
	comments: {
		editorConfig: {
			extraPlugins: [Autoformat, Bold, Italic],
		},
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableCellProperties',
			'tableProperties',
		],
		tableToolbar: ['comment'],
	},
	wproofreader: {
		lang: 'auto',
		serviceId: process.env.VUE_APP_CKEDITOR_WPROOFREADER_SERVICE_ID,
		srcUrl: 'https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js',
	},
};

Editor.defaultConfig = defaultEditorConfig;

export const editor = Editor;

export const revisionHistoryPlugins = [RevisionHistory, RevisionsRepository];
